import { ArrowRight, Eye, EyeOff, RotateCcw, Rocket } from '@orgus/ui/icons';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button, ConfirmDialog, ErrorState, Pressable, Skeleton, toUiError, toast } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { loadOnboarding, updateOnboarding, useOnboarding, useOnboardingData } from '../../onboarding/client.ts';
import { NEXT_ACTION } from '../../onboarding/model.ts';
import { ProgressBar, StepStatusIcon } from '../../onboarding/parts.tsx';
import { useOnboardingView } from '../../onboarding/useOnboardingView.ts';
import type { SettingsSection } from '../registry.ts';

function GettingStartedSettings() {
  const navigate = useNavigate();
  useOnboardingData();
  const view = useOnboardingView();
  const state = useOnboarding((s) => s.state);
  const error = useOnboarding((s) => s.error);
  const role = useOnboarding((s) => s.probes.auth?.me?.role ?? 'owner');
  const [confirmReset, setConfirmReset] = useState(false);
  /** Why showing or hiding the checklist failed: shown under the buttons, never in a toast. */
  const [toggleError, setToggleError] = useState<UiError | null>(null);

  if (view.phase === 'loading') {
    return (
      <div className="flex flex-col gap-3" role="status" aria-label="Loading Getting started">
        <Skeleton className="h-4 w-64" />
        <Skeleton className="h-48" />
      </div>
    );
  }
  if (view.phase === 'error') return <ErrorState error={error} onRetry={() => loadOnboarding()} />;

  const hidden = state.dismissedAt != null;
  const shown = view.showChecklist && !hidden;
  const toggle = async () => {
    setToggleError(null);
    try {
      await updateOnboarding({ op: hidden ? 'reopen' : 'dismiss' });
      toast.success(hidden ? 'Checklist is back on Overview' : 'Checklist hidden', hidden ? undefined : 'Bring it back here at any time.');
    } catch (e) {
      setToggleError(toUiError(e));
    }
  };
  const reset = async () => {
    await updateOnboarding({ op: 'reset' });
    toast.success('Getting started starts over', 'Skips and marks are cleared. Steps the hub can see stay done.');
  };

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <div className="flex flex-col gap-3 rounded-card border border-border bg-surface p-4">
        <ProgressBar done={view.done} total={view.total} />
        <ol className="flex flex-col">
          {view.steps.map((s) => (
            <li key={s.id}>
              <Pressable
                onClick={() => navigate(`/getting-started?step=${s.id}`)}
                className="-mx-2 flex w-[calc(100%+1rem)] items-center gap-3 rounded-control px-2 py-2 text-left hover:bg-hover"
              >
                <StepStatusIcon status={s.status} bare />
                <span className="flex-1 text-ui text-fg">{s.title}</span>
                <span className="text-caption text-fg-subtle">{s.detail}</span>
              </Pressable>
            </li>
          ))}
        </ol>
        <div className="flex flex-wrap items-center gap-2 border-t border-border-subtle pt-3">
          <Button variant="primary" size="sm" icon={<Rocket />} iconRight={<ArrowRight />} onClick={() => navigate(view.current ? `/getting-started?step=${view.current}` : '/getting-started')}>
            {view.current ? NEXT_ACTION[view.current] : 'Open Getting started'}
          </Button>
          <span className="flex-1" />
          {!view.complete && role !== 'viewer' && (
            <Button size="sm" variant="ghost" icon={hidden ? <Eye /> : <EyeOff />} onClick={toggle}>
              {hidden ? 'Show checklist on Overview' : 'Hide checklist on Overview'}
            </Button>
          )}
          {(role === 'owner' || role === 'admin') && (
            <Button size="sm" variant="ghost" icon={<RotateCcw />} onClick={() => setConfirmReset(true)}>
              Start over
            </Button>
          )}
        </div>
        {toggleError && <ErrorState variant="inline" error={toggleError} onRetry={toggle} />}
      </div>
      <p className="text-caption text-fg-subtle">
        {view.complete
          ? 'Every step is done or skipped, so Overview no longer shows the checklist.'
          : shown
            ? 'Overview shows the checklist until every step is done or skipped.'
            : hidden
              ? 'The checklist is hidden on Overview.'
              : 'Overview shows the checklist on a new hub, or once you open Getting started.'}
      </p>
      <ConfirmDialog
        open={confirmReset}
        onOpenChange={setConfirmReset}
        title="Start Getting started over?"
        description="Skipped steps and steps you marked done by hand come back, the saved phone address is cleared, and the checklist shows on Overview again. Steps the hub can see are done (a connected machine, a watched repo) stay done."
        confirmLabel="Start over"
        tone="primary"
        onConfirm={reset}
      />
    </div>
  );
}

const section: SettingsSection = {
  id: 'getting-started',
  title: 'Getting started',
  description: 'The first-ten-minutes checklist, and whether Overview shows it.',
  order: 5,
  group: 'workspace',
  keywords: ['onboarding', 'checklist', 'setup', 'first run'],
  icon: Rocket,
  Component: GettingStartedSettings,
};
export default section;
